"use client";

import React, { useRef, useCallback, useState } from 'react';
import styles from './_gc-step-status-info.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputCalendar } from '@/components/form';
import Modal from '@/components/modal';
import { GC_CALC_POPULATION, GC_CALC_STATUS } from '@/data/gc-calculator-data';
import { validatePresentOrFutureDate, parseMDY, addDaysToDate } from '../../utils';

// ---------------------------------------------------------------------------
// Early-start date helpers
// ---------------------------------------------------------------------------

/** Returns true when the expected start date is less than 4 weeks from today. */
function isStartDateWithin4Weeks(dateStr) {
  const startDate = parseMDY(dateStr);
  if (!startDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(today.getDate() + 28);
  return startDate < cutoff;
}

const CONTENT = {
  [GC_CALC_STATUS.STARTING]: {
    title: 'Starting CRENESSITY',
    showExpectedStartDate: true,
    paragraphs: [
      'Advise your patients to take CRENESSITY 100 mg twice daily with a meal or snack containing at least 5 g of fat to aid absorption. Absorption is similar with 150\u2013350 calories. Some examples with the fat and calories for maximal absorption are: a glass of 2% or whole milk, full-fat oat milk, and crackers with cheese or peanut butter.',
      'Pick a consistent time for drawing labs and obtain baseline labs if not already available. Pre-morning GC dose labs are preferred. Assess labs \u22654 weeks after initiation of CRENESSITY to inform GC reduction. Labs you may need to obtain when reducing GCs include: ACTH, 17-OHP, A4, testosterone, renin, and electrolytes.'
    ],
    pediatricParagraphs: [
      'Start CRENESSITY using weight-based dosing. Advise your patients to take CRENESSITY twice daily with a meal or snack containing at least 5 g of fat to aid absorption. Absorption is similar with 150\u2013350 calories. Some examples with the fat and calories for maximal absorption are: a glass of 2% or whole milk, full-fat oat milk, and crackers with cheese or peanut butter.',
      'Pick a consistent time for drawing labs and obtain baseline labs if not already available. Pre-morning GC dose labs are preferred. Assess labs ≥4 weeks after initiation of CRENESSITY to inform GC reduction. Labs you may need to obtain when reducing GCs include: ACTH, 17-OHP, A4, testosterone, renin, and electrolytes.',
    ],
  },
  [GC_CALC_STATUS.CONTINUING]: {
    title: 'Continuing CRENESSITY',
    showExpectedStartDate: false,
    paragraphs: [
      'Remember to pick a consistent time for drawing labs. Pre-morning GC dose labs are preferred. Labs you may need to obtain when reducing GCs include: ACTH, 17-OHP, A4, testosterone, renin, and electrolytes.',
    ],
  },
};

/**
 * @description GC OPTIMIZE™ Calculator — Status Info interstitial
 * Shown after Step 1 (Patient Profile), before Patient Expectations.
 * Renders "Starting CRENESSITY" or "Continuing CRENESSITY" guidance
 * based on state.status.
 *
 * Starting path includes an "Expected start date" calendar input.
 * Continuing path is info-only — no form inputs.
 *
 * Figma: 14681:20607
 */
function GcStepStatusInfo({ state, dispatch, onNext, stepNumber, innerStep }) {
  const content = CONTENT[state.status] ?? CONTENT[GC_CALC_STATUS.STARTING];
  const shouldCaptureExpectedStartDate = !!content.showExpectedStartDate;
  const paragraphs =
    state.status === GC_CALC_STATUS.STARTING && state.population === GC_CALC_POPULATION.PEDIATRIC
      ? content.pediatricParagraphs ?? content.paragraphs
      : content.paragraphs;
  const expectedDateRef = useRef(state.formValues?.expectedStartDate ?? '');
  const expectedDateCalRef = useRef(null);
  const modalRef = useRef(null);
  const [isDateValid, setIsDateValid] = useState(
    shouldCaptureExpectedStartDate ? !!(state.formValues?.expectedStartDate ?? '').trim() : true
  );
  const [earlyStartReturnDate, setEarlyStartReturnDate] = useState('');

  const validateExpectedStartDate = useCallback(() => {
    return validatePresentOrFutureDate((expectedDateRef.current || '').trim(), expectedDateCalRef, { allowToday: true });
  }, []);

  const handleSave = useCallback(() => {
    if (shouldCaptureExpectedStartDate) {
      if (!validateExpectedStartDate()) {
        return;
      }
      dispatch({ type: 'SET_FIELD', field: 'expectedStartDate', value: expectedDateRef.current });
      // If the expected start date is less than 4 weeks from today, the patient
      // will not yet have had 4 weeks on CRENESSITY — show the blocking modal
      // and end the calculator flow for this session.
      if (isStartDateWithin4Weeks(expectedDateRef.current)) {
        setEarlyStartReturnDate(addDaysToDate(expectedDateRef.current, 28));
        modalRef.current?.open();
        return;
      }
    }
    onNext();
  }, [dispatch, onNext, shouldCaptureExpectedStartDate, validateExpectedStartDate]);

  return (
    <>
    <GcStepShell
      title={content.title}
      onNext={handleSave}
      showBack={false}
      nextLabel="Save &amp; continue"
      isNextDisabled={shouldCaptureExpectedStartDate && !isDateValid}
      stepNumber={stepNumber}
      innerStep={innerStep}
    >
      <div className={styles.body}>
        {paragraphs.map((text, i) => (
          <p key={i} className={styles.paragraph}>{text}</p>
        ))}

        {shouldCaptureExpectedStartDate && (
          <FormInputCalendar
            ref={expectedDateCalRef}
            name="gc-expected-start-date"
            label="Expected start date"
            disableDates="past"
            defaultValue={state.formValues?.expectedStartDate ?? ''}
            errorMessage="Please select a date in the future."
            yearDropdownYearsBack={5}
            customClass={styles.date_input}
            onChange={(e) => {
              expectedDateRef.current = e.target.value;
              const valid = validateExpectedStartDate();
              setIsDateValid(!!valid);
            }}
          />
        )}
      </div>
    </GcStepShell>

    {/* Early-start blocking modal — shown when expected start date < 4 weeks from today */}
    <Modal
      ref={modalRef}
      customClass={styles.early_start_modal}
      ariaLabelledby="gc-early-start-modal-heading"
      ariaDescribedby="gc-early-start-modal-desc"
      onClose={() => dispatch({ type: 'RESET' })}
    >
      <div className={styles.modal_body}>
        <p id="gc-early-start-modal-heading" className={styles.modal_text}>
          Once your patient has been on CRENESSITY for{' '}
          <strong>at least 4 weeks,</strong>
          {' '}A4 levels can be assessed to inform GC dose reductions.
        </p>
        <p id="gc-early-start-modal-desc" className={styles.modal_text}>
          Please come back to GC OPTIMIZE&trade; after{' '}
          <span className={styles.modal_date}>{earlyStartReturnDate}</span>
          {' '}to continue with your plan.
        </p>
      </div>
    </Modal>
    </>
  );
}

export default GcStepStatusInfo;
